import dayjs from "dayjs";

import {
  flexpa__EndpointMode,
  flexpa__LinkDeactivatedReason,
  type GetInsuranceLinksQuery,
  ingestion__IngestionStatus,
} from "@medbillai/graphql-types";

import { formatPhoneNumberValue } from "@medbillai/ui";

import type {
  EventTypes,
  InsuranceEventNames,
} from "../../../lib/analytics/types";
import type { ErrorState } from "../../../lib/insurance-link/insuranceStore";
import type { Endpoint } from "../../../lib/insurance-link/types";
import type { AlertDialog } from "../../../provider/alerts";
import { insurerAuthLinks } from "./consts";

export const INSURANCE_CARD_POLL_INTERVAL_MS = 2000;

export const FLEXPA_LINK_WAIT_TIME_MINUTES = 10;
export const WEB_AGENT_INGESTION_WAIT_TIME_MINUTES = 60;
export const WEB_AGENT_CRAWL_WAIT_TIME_MINUTES = 15;

export const resultUnavailableKey = "resultUnavailable";
export const resultUnavailablePlaceholder: Endpoint = {
  __typename: "flexpa__Endpoint_Type",
  displayName: resultUnavailableKey,
  id: resultUnavailableKey,
  slug: "PlaceHolder",
  isActive: true,
  organization: {
    __typename: "integration__Organization_Type",
    id: "PlaceHolder",
    displayName: "PlaceHolder",
    slug: "PlaceHolder",
  },
  flexpaId: "PlaceHolder",
  mode: flexpa__EndpointMode.LIVE,
  name: "PlaceHolder",
};

export const isValidFunction = (func: unknown): func is () => void =>
  typeof func === "function";

/**
 * Computes the overall status of a web agent link based on its ingestions.
 * Takes a conservative approach where if any ingestion is completed,
 * the whole thing is considered complete.
 */
export function computeWebAgentLinkStatusFromIngestions(
  ingestions: { status: ingestion__IngestionStatus }[] | null | undefined,
): ingestion__IngestionStatus | undefined {
  const statuses = (ingestions || [])
    .map(ingestion => ingestion.status)
    .filter(status => !!status);

  let status: ingestion__IngestionStatus | undefined;
  if ((statuses?.length ?? 0) > 0) {
    // We are conservative with status - if there is even a single
    // completed ingestion, we consider the overall link status to be
    // "complete" - this is to prevent us from ever showing the
    // "importing" screen after we have notified the user that we got
    // their bills. It is technically OVER-conservative, because we
    // might stop showing the screen BEFORE we complete the ingestion
    // that is going to provide the bills.
    if (statuses.some(s => s === ingestion__IngestionStatus.COMPLETED)) {
      status = ingestion__IngestionStatus.COMPLETED;
    } else if (statuses.some(s => s === ingestion__IngestionStatus.PARTIAL)) {
      // If there is a partial ingestion, we consider the overall link status to be
      // "complete" - because this is also a valid end state for the ingestion.
      // We return "completed" because there would be no difference between the
      // result loaded state.
      status = ingestion__IngestionStatus.COMPLETED;
    } else if (
      statuses.some(s => s === ingestion__IngestionStatus.IN_PROGRESS)
    ) {
      // If nothing is completed, but something is in progress, then
      // consider the whole thing to be "in progress".
      status = ingestion__IngestionStatus.IN_PROGRESS;
    } else if (statuses.some(s => s === ingestion__IngestionStatus.FAILED)) {
      // Finally, if something has failed, consider the whole thing
      // failed.
      status = ingestion__IngestionStatus.FAILED;
    }
    // Otherwise, we are probably missing a status - fall through to the
    // condition below.
  }
  return status;
}

/**
 * Creates an alert dialog with customizable title and message
 */
export const createExitAlert = (
  title: string,
  message: string,
  redirect: () => void,
): AlertDialog => ({
  title,
  message:
    message ||
    "Are you sure you want to exit? Your progress will not be saved.",
  acceptText: "Exit",
  cancellable: true,
  onAccept: redirect,
});

export const closeInsurancePreemptiveAlert = (
  redirect: () => void,
): AlertDialog =>
  createExitAlert(
    "Exit connecting insurance?",
    "Are you sure you want to exit? Your progress will not be saved.",
    redirect,
  );

export const closeMmfaPreemptiveAlert = (redirect: () => void): AlertDialog =>
  createExitAlert(
    "Exit setting up Trusted Authentication?",
    "Are you sure you want to exit? Your progress will not be saved.",
    redirect,
  );

export const closeMmfaDisablePreemptiveAlert = (
  redirect: () => void,
): AlertDialog =>
  createExitAlert(
    "Exit disabling Trusted Authentication?",
    "Are you sure you want to exit? Your progress will not be saved.",
    redirect,
  );

export const restartInsuranceAlert = (redirect: () => void): AlertDialog => {
  const alert: AlertDialog = {
    title: "Restart connecting insurance?",
    message: "Are you sure you want to exit? Your progress will not be saved.",
    acceptText: "Back",
    cancellable: true,
    onAccept: () => {
      redirect();
    },
  };
  return alert;
};

const successfulStatuses: InsuranceLinkStatus["_tag"][] = [
  "COMPLETED",
  "PARTIAL",
  "PREVIOUSLY_COMPLETED",
];

export type InsuranceLinkStatus =
  | {
      _tag: "COMPLETED";
      endedAt: Date;
    }
  | {
      _tag: "PARTIAL";
      endedAt: Date;
    }
  | {
      _tag: "PREVIOUSLY_COMPLETED";
      endedAt: Date | null;
    }
  | {
      _tag: "EXPIRED";
    }
  | {
      _tag: "FAILED";
    }
  | {
      _tag: "CRAWL_IN_PROGRESS";
    }
  | {
      _tag: "CRAWL_TIMEOUT";
    }
  | {
      _tag: "INGESTION_IN_PROGRESS";
      ingested: { id: string }[];
    }
  | { _tag: "DEACTIVATED"; id: string };

export const getWebLinkStatus = (
  link: NonNullable<
    NonNullable<GetInsuranceLinksQuery["me"]>["webAgentLinks"]
  >[0],
): InsuranceLinkStatus => {
  if (link.deactivatedAt) {
    return {
      _tag: "DEACTIVATED",
      id: link.id,
    };
  }
  // If the current latest execution is completed or partial, we can return the
  // status from the latest execution.
  const latestExecutionStatus = computeWebAgentLinkStatusFromIngestions(
    link.latestExecution?.ingestions,
  );
  const recentExecution =
    dayjs().diff(link.createdAt, "minutes") <
    WEB_AGENT_INGESTION_WAIT_TIME_MINUTES;

  if (!latestExecutionStatus && recentExecution) {
    return {
      _tag: "CRAWL_IN_PROGRESS",
    };
  }

  if (
    latestExecutionStatus === ingestion__IngestionStatus.COMPLETED ||
    latestExecutionStatus === ingestion__IngestionStatus.PARTIAL
  ) {
    // Prefer lastSyncedAt if available, otherwise use the ingestion's endedAt
    // The ingestion endedAt is the actual completion time, not the current time
    const latestCompletedIngestion = link.latestExecution?.ingestions?.find(
      ingestion =>
        ingestion.status === ingestion__IngestionStatus.COMPLETED ||
        ingestion.status === ingestion__IngestionStatus.PARTIAL,
    );
    const endedAt = link.lastSyncedAt
      ? dayjs(link.lastSyncedAt).toDate()
      : latestCompletedIngestion?.endedAt
        ? dayjs(latestCompletedIngestion.endedAt).toDate()
        : undefined;

    // TODO: this scenario is absurd. We should not have a case where we have a
    // successful ingestion but no endedAt. Ideally we should update data schema
    // to reflect that.
    // https://linear.app/medbillai/issue/ENG-5922/define-single-source-of-truth-for-insurance-link-status-and-use-it-for
    if (!endedAt) {
      console.error(
        `absurd: No endedAt found for successful ingestion. Link: ${link.id}, Ingestion: ${latestCompletedIngestion?.id}. This is a bug in granted code.`,
      );
      return {
        _tag: "FAILED",
      };
    }

    return {
      _tag: "COMPLETED",
      endedAt,
    };
    // If the current latest execution is in progress, we can return the status from the latest execution.
  } else if (latestExecutionStatus === ingestion__IngestionStatus.IN_PROGRESS) {
    return {
      _tag: "INGESTION_IN_PROGRESS",
      ingested:
        link.latestExecution?.ingestions?.flatMap(
          ingestion => ingestion.resources || [],
        ) || [],
    };
  }

  // We now know that there was an issue with the latest execution, we need to
  // check the previous executions to determine if they were completed
  // The query orders executions by startedAt DESC and filters ingestions for COMPLETED/PARTIAL
  const mostRecentCompletedExecution = link.executions?.find(
    execution => execution.ingestions && execution.ingestions.length > 0,
  );

  if (mostRecentCompletedExecution?.ingestions?.[0]) {
    return {
      _tag: "PREVIOUSLY_COMPLETED",
      endedAt: mostRecentCompletedExecution.ingestions[0].endedAt
        ? dayjs(mostRecentCompletedExecution.ingestions[0].endedAt).toDate()
        : new Date(),
    };
  }
  return {
    _tag: "FAILED",
  };
};

export const getFlexpaLinkStatus = (
  link: NonNullable<
    NonNullable<GetInsuranceLinksQuery["me"]>["flexpaLinks"]
  >[0],
): InsuranceLinkStatus => {
  if (link.active && !link.lastSyncedAt) {
    if (
      dayjs().diff(link.createdAt, "minutes") < FLEXPA_LINK_WAIT_TIME_MINUTES
    ) {
      return {
        _tag: "CRAWL_IN_PROGRESS",
      };
    } else {
      return {
        _tag: "FAILED",
      };
    }
  }
  if (link.active && link.lastSyncedAt) {
    return {
      _tag: "COMPLETED",
      endedAt: new Date(link.lastSyncedAt),
    };
  }
  if (link.deactivatedReason === flexpa__LinkDeactivatedReason.EXPIRED) {
    return {
      _tag: "EXPIRED",
    };
  }
  return {
    _tag: "FAILED",
  };
};
// These are routes we do not pop a confirm dialog for closing the flow
const closeUnprotectedRoutes = [
  "success",
  "alternate-insurance",
  "exit-survey",
  "exit-survey-extra",
];

export function closeInsuranceFlow(
  nav: unknown,
  showAlert: (alert: AlertDialog) => void,
  routeName: string,
  trackEvent: <E extends InsuranceEventNames>(
    eventName: E,
    properties: EventTypes[E],
  ) => void,
  customAlert?: (redirect: () => void) => AlertDialog,
) {
  if (!isValidFunction(nav)) {
    // There is 0 reason this should happen but we have no other way of
    // validating the nav type
    throw new Error(
      `Error routing close during insurance flow, navigator not a valid function on route: ${routeName}`,
    );
  }
  trackEvent("InsuranceLinkCloseStep", {
    initialRoute: routeName,
  });
  console.log(routeName);
  if (closeUnprotectedRoutes.includes(routeName)) {
    nav();
  } else {
    showAlert(
      customAlert ? customAlert(nav) : closeInsurancePreemptiveAlert(nav),
    );
  }
}

// Additional routes that we do not want to show a confirm dialog for going back
const backUnprotectedRoutes = ["login-info-ready", "exit-survey-extra"];

export function goBackInsuranceFlow(
  nav: unknown,
  showAlert: (alert: AlertDialog) => void,
  routeName: string,
  trackEvent: <E extends InsuranceEventNames>(
    eventName: E,
    properties: EventTypes[E],
  ) => void,
) {
  if (!isValidFunction(nav)) {
    // There is 0 reason this should happen but we have no other way of
    // validating the nav type
    throw new Error(
      `ERROR: error routing back during insurance flow, navigator not a valid function on route: ${routeName}`,
    );
  }
  trackEvent("InsuranceLinkBackStep", {
    initialRoute: routeName,
  });
  if (
    closeUnprotectedRoutes.concat(backUnprotectedRoutes).includes(routeName)
  ) {
    nav();
  } else {
    showAlert(restartInsuranceAlert(nav));
  }
}

export function getErrorCopy(
  isRelinking: boolean,
  errorState: ErrorState | null,
) {
  if (isRelinking) {
    return {
      title: "Could not update your account",
      subtitle:
        "We're unable to re-link your insurance account at the moment. Please, try again later.",
      description:
        "In the meantime, please go ahead and start a case. Our team of experts will do their best to support you with your issue.",
    };
  } else if (errorState && errorState.type === "FailedLoginMaxReached") {
    return {
      title: "Could not log in",
      subtitle: "Try resetting your password at ",
      description:
        "In the meantime, please go ahead and start a case. Our team of experts will do their best to support you with your issue.",
      insurerLink: errorState.signInUrl,
    };
  } else if (errorState?.type === "Unknown" && errorState.signInUrl) {
    return {
      title: "We had trouble logging in",
      subtitle: "Try confirming you can sign in at ",
      description:
        "In the meantime, please go ahead and start a case. Our team of experts will do their best to support you with your issue.",
      insurerLink: errorState.signInUrl,
    };
  } else {
    return {
      title: "Insurer not responding",
      subtitle: "Your insurer is taking longer than usual.",
      description:
        "Don't worry – you can still continue setting up your account.",
    };
  }
}

export type InsuranceStatus =
  | "COMPLETED"
  | "PARTIAL"
  | "EXPIRED"
  | "FAILED"
  | "CRAWL_IN_PROGRESS"
  | "CRAWL_TIMEOUT"
  | "INGESTION_IN_PROGRESS"
  | "DEACTIVATED";

export type ProcessedInsuranceLink = {
  id: string;
  status: InsuranceLinkStatus;
  endedAt?: Date;
  ingested?: { id: string }[];
  memberName: string;
  insuranceSlug: string;
  relinkId?: string;
  createdAt: Date;
  lastSyncedAt?: Date;
  mmfaEnabled?: boolean;
  mmfaAvailable?: boolean;
  mmfaPhoneNumber?: string;
};

export function useProcessedInsuranceLinks(
  flexpaLinks: NonNullable<
    NonNullable<GetInsuranceLinksQuery["me"]>["flexpaLinks"]
  >,
  webAgentLinks: NonNullable<
    NonNullable<GetInsuranceLinksQuery["me"]>["webAgentLinks"]
  >,
): ProcessedInsuranceLink[] {
  return [...flexpaLinks, ...webAgentLinks]
    .map(link => {
      if (link.__typename === "webAgent__Link_Type") {
        const webStatus = getWebLinkStatus(link);
        // Determine if relink is needed: always for incomplete statuses,
        // or if successful but more than 1 day old
        const isSuccessful = successfulStatuses.includes(webStatus._tag);
        const lastSuccessfulSyncDate =
          isSuccessful && "endedAt" in webStatus
            ? (webStatus.endedAt ?? undefined)
            : undefined;
        // If the link is successful, we only want to relink if the last successful sync was more than 1 day ago
        const resync =
          !isSuccessful ||
          !lastSuccessfulSyncDate ||
          dayjs().diff(lastSuccessfulSyncDate, "day") > 1;
        return {
          id: link.id,
          status: webStatus,
          endedAt:
            webStatus._tag === "COMPLETED" || webStatus._tag === "PARTIAL"
              ? webStatus.endedAt
              : undefined,
          ingested:
            webStatus._tag === "INGESTION_IN_PROGRESS"
              ? webStatus.ingested
              : undefined,
          memberName: link.patientName || "",
          insuranceSlug: link.endpoint.slug || "",
          relinkId: resync ? link.id : undefined,
          createdAt: new Date(link.createdAt),
          lastSyncedAt: lastSuccessfulSyncDate,
          mmfaEnabled: link.hasManagedMfa,
          mmfaAvailable:
            link.endpoint.managedMfaConfiguration?.isSupported ?? false,
          mmfaPhoneNumber:
            link.mfaReceiver?.__typename === "webAgent__MfaNumber_Type"
              ? (formatPhoneNumberValue(link.mfaReceiver.backupNumber) ??
                undefined)
              : undefined,
        };
      }
      if (link.__typename === "flexpa__Link_Type") {
        const flexpaStatus = getFlexpaLinkStatus(link);
        return {
          id: link.id,
          status: flexpaStatus,
          endedAt:
            flexpaStatus._tag === "COMPLETED"
              ? flexpaStatus.endedAt
              : undefined,
          memberName: link.patientName || "",
          insuranceSlug:
            link.endpoint.slug || link.endpoint.organization?.slug || "",
          createdAt: new Date(link.createdAt),
          lastSyncedAt: link.lastSyncedAt
            ? new Date(link.lastSyncedAt)
            : undefined,
        };
      }

      return undefined;
    })
    .filter(link => link !== undefined)
    .sort((a, b) => {
      if (a.lastSyncedAt && b.lastSyncedAt) {
        return b.lastSyncedAt.getTime() - a.lastSyncedAt.getTime();
      }

      if (a.lastSyncedAt) {
        return 1;
      }
      if (b.lastSyncedAt) {
        return -1;
      }

      return b.createdAt.getTime() - a.createdAt.getTime();
    });
}

interface InsurerHelpUrls {
  forgotPasswordUrl: string | null;
  registerUrl: string | null;
}

/**
 * Gets the help URLs for an insurance endpoint.
 * Prefers manually verified URLs from insurerAuthLinks, falls back to endpoint URLs.
 */
export function getInsurerHelpUrls(endpoint: Endpoint | null): InsurerHelpUrls {
  if (endpoint?.__typename !== "webAgent__Endpoint_Type") {
    return { forgotPasswordUrl: null, registerUrl: null };
  }

  const authLinks = endpoint.slug ? insurerAuthLinks[endpoint.slug] : undefined;

  return {
    forgotPasswordUrl:
      authLinks?.forgotPasswordUrl ?? endpoint.loginUrl ?? null,
    registerUrl: authLinks?.registerUrl ?? null,
  };
}
